import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Box, CircularProgress,
  Divider, Typography
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MdEdit } from 'react-icons/md';
import { getStates } from '../../../services/locations/getStates';
import { getCities } from '../../../services/locations/getCities';
import axiosInstance from '../../../services/axiosMiddleware';

const DESIGNATIONS = [
  'Principal', 'Vice Principal', 'HOD', 'Coordinator',
  'Director', 'Dean', 'Manager', 'Administrator', 'Supervisor', 'Other'
];

const EditManagerDialog = ({ open, onClose, manager, onSuccess }) => {
  const [form, setForm] = useState({
    authname: '',
    email: '',
    phone: '',
    organization_name: '',
    designation: '',
    stateid: '',
    cityid: '',
    address: '',
  });


  console.log("Manager Data:", manager);
console.log("Form State:", form);

  // Populate form when dialog opens with manager data
  useEffect(() => {
    if (manager && open) {
      setForm({
        authname: manager.name || '',
        email: manager.email || '',
        phone: manager.phone || '',
        organization_name: manager.organization_name || '',
        designation: manager.designation || '',
        stateid: manager.stateid ? String(manager.stateid) : '',
        cityid: manager.cityid ? String(manager.cityid) : '',
        address: manager.address || '',
      });
    }
  }, [manager, open]);

  const { data: statesData } = useQuery({
    queryKey: ['states'],
    queryFn: () => getStates(101),
  });
  const states = statesData?.data || [];

  const { data: citiesData } = useQuery({
    queryKey: ['cities', form.stateid],
    queryFn: () => getCities(form.stateid),
    enabled: !!form.stateid,
  });
  const cities = citiesData?.data || [];

  const updateMutation = useMutation({
    mutationFn: (payload) => axiosInstance.put(`users/${manager?.userid}/update`, payload).then(r => r.data),
    onSuccess: (data) => {
      if (!data.status) {
        toast.error(data.message);
        return;
      }
      toast.success('Manager updated successfully');
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to update manager');
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      if (name === 'stateid') {
        return { ...prev, stateid: value, cityid: '' };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleSubmit = () => {
    if (!form.authname?.trim()) return toast.error('Name is required');
    if (!form.email?.trim()) return toast.error('Email is required');
    if (!form.phone?.trim()) return toast.error('Phone is required');
    if (!form.organization_name?.trim()) return toast.error('Organization name is required');

    updateMutation.mutate({
      authname: form.authname,
      email: form.email,
      phone: form.phone,
      organization_name: form.organization_name,
      designation: form.designation || null,
      stateid: form.stateid ? Number(form.stateid) : null,
      cityid: form.cityid ? Number(form.cityid) : null,
      address: form.address || null,
    });
  };

  if (!manager) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MdEdit size={22} />
          Edit Manager
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>

          <Typography variant="caption" color="text.secondary">Account Details</Typography>
          <Divider sx={{ mt: -1 }} />

          <TextField label="Full Name" name="authname" value={form.authname} onChange={handleChange} fullWidth />
          <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} fullWidth />
          <TextField label="Phone Number" name="phone" value={form.phone} onChange={handleChange} fullWidth />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Organization Details</Typography>
          <Divider sx={{ mt: -1 }} />

          <TextField label="Organization Name" name="organization_name" value={form.organization_name} onChange={handleChange} fullWidth />

          <TextField select label="Designation (optional)" name="designation" value={form.designation} onChange={handleChange} fullWidth>
            <MenuItem value=""><em>Select Designation</em></MenuItem>
            {DESIGNATIONS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* State Dropdown */}
            <TextField
              select
              label="State"
              name="stateid"
              value={form.stateid}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value=""><em>Select State</em></MenuItem>
              {states.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>

            {/* City Dropdown */}
            <TextField
              select
              label="City"
              name="cityid"
              value={form.cityid}
              onChange={handleChange}
              fullWidth
              disabled={!form.stateid}
            >
              <MenuItem value=""><em>Select City</em></MenuItem>
              {cities.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            label="Address (optional)"
            name="address"
            value={form.address}
            onChange={handleChange}
            multiline
            rows={2}
            fullWidth
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={updateMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updateMutation.isPending}
          startIcon={updateMutation.isPending ? <CircularProgress size={16} /> : null}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditManagerDialog;